import {
  CampaignMedium,
  MarketingCampaigns,
  MarketingMediumTemplates,
  PaymentCustomerAddOnPayments,
} from '@prisma/client';
import API_URL from '../apiconfig';
import {
  CampaignSegment,
  CreateTemplateAndEmbedResponse,
  FinalCampaignSegment,
  GetMarketingDataParams,
  MarketingBusinessesWithCampaigns,
  MarketingCampaignSegmentWithFilters,
  MarketingEmailTemplateWithSource,
  MarketingSmsTemplateWithSource,
  SegmentCustomer,
  CreateUpdateCampaignPayload,
  UpsertCampaignPayload,
  SmsContentSchema,
  EmailContentSchema,
  CreateUpdateDraftCampaignPayload,
  PocketflowsEmailAddress,
  PocketflowsProjectEmailTemplate,
  PocketflowsCampaign,
  MarketingCampaignWithSource,
  CampaignCost,
  PocketflowsCustomer,
  PurchaseCreditsPayload,
  CreateTemplateEmbedResponse,
  EmployeeCredits,
  CampaignMetrics,
} from '../types/marketing';
import axios from './api';
import { getApiErrorMessage } from './apiError';
import client from '../utils/react-query/client';

export async function getMarketingData(params: GetMarketingDataParams) {
  try {
    const marketingData = await axios.get(`${API_URL}/v1/marketing`, {
      params,
    });

    return marketingData.data.result as MarketingBusinessesWithCampaigns;
  } catch (error) {
    throw new Error('Cannot get employee', { cause: error });
  }
}

export async function upsertCampaign(
  marketingBusinessId: string,
  payload: CreateUpdateCampaignPayload | CreateUpdateDraftCampaignPayload,
  draft?: boolean,
) {
  try {
    const templateId =
      payload.medium === 'SMS'
        ? (payload?.content as SmsContentSchema)?.smsTemplateId
        : payload.medium === 'EMAIL'
        ? (payload?.content as EmailContentSchema)?.emailTemplateId
        : null;
    if (!templateId && !draft) throw new Error('Invalid template');

    // Prebuilt templates have an string id, custom templates have an integer id
    const includeSegmentId = Number.isInteger(payload.includeSegment?.id)
      ? (payload.includeSegment?.id as number)
      : null;
    const excludeSegmentId = Number.isInteger(payload.excludeSegment?.id)
      ? (payload.excludeSegment?.id as number)
      : null;
    const prebuiltIncludeSegmentId = !Number.isInteger(
      payload.includeSegment?.id,
    )
      ? (payload.includeSegment?.id as string)
      : null;
    const prebuiltExcludeSegmentId = !Number.isInteger(
      payload.excludeSegment?.id,
    )
      ? (payload.excludeSegment?.id as string)
      : null;

    const sendTime =
      draft && payload.sendTime && !Object.keys(payload.sendTime).length
        ? null
        : payload.sendTime;

    const body: UpsertCampaignPayload = {
      id: payload.id,
      name: payload.name,
      medium: payload.medium,
      sendTime,
      templateId,
      includeSegmentId,
      prebuiltIncludeSegmentId,
      includeSegment: payload.includeSegment,
      excludeSegmentId,
      prebuiltExcludeSegmentId,
      excludeSegment: payload.excludeSegment,
      isFinal: !draft,
      emailAddressId: payload.emailAddressId,
      offboardingId: payload.offboardingId,
    };

    const campaign = await axios.post(
      `${API_URL}/v1/marketing/${marketingBusinessId}/campaign`,
      body,
    );

    return {
      campaign: campaign.data.result?.campaign as MarketingCampaignWithSource,
    };
  } catch (error) {
    return {
      error: getApiErrorMessage(error, 'Cannot create campaign'),
    };
  }
}

export async function deleteCampaign(
  marketingBusinessId: string,
  campaignId: string,
) {
  try {
    await axios.delete(
      `${API_URL}/v1/marketing/${marketingBusinessId}/campaign/${campaignId}`,
    );
  } catch (error) {
    throw new Error('Cannot delete campaign', { cause: error });
  }
}

export async function stopCampaign(
  marketingBusinessId: string,
  campaignId: string,
) {
  try {
    const result = await axios.post(
      `${API_URL}/v1/marketing/${marketingBusinessId}/campaign/${campaignId}/stop`,
    );

    return result.data.result as { campaign: MarketingCampaigns | null };
  } catch (error) {
    throw new Error('Cannot stop campaign', { cause: error });
  }
}

export async function getSegmentCustomers(
  marketingBusinessId: string,
  data: CampaignSegment,
) {
  try {
    const customers = await axios.post(
      `${API_URL}/v1/marketing/${marketingBusinessId}/segment-customers`,
      { ...data, take: 50 },
    );

    return customers.data.result as {
      totalCount: number;
      customers: SegmentCustomer[];
      sourceCustomers: PocketflowsCustomer[];
    };
  } catch (error) {
    throw new Error('Cannot get segment customers', { cause: error });
  }
}

export async function getCombinedSegmentCustomers(
  marketingBusinessId: string,
  data: {
    includeSegment: CampaignSegment | null;
    excludeSegment: CampaignSegment | null;
    medium: CampaignMedium | null;
  },
) {
  try {
    const customers = await axios.post(
      `${API_URL}/v1/marketing/${marketingBusinessId}/segment-customers/combined`,
      data,
    );

    return customers.data.result as {
      totalCount: number;
    };
  } catch (error) {
    throw new Error('Cannot get segment customers', { cause: error });
  }
}

export async function getSegments(marketingBusinessId: string) {
  try {
    const segments = await axios.get(
      `${API_URL}/v1/marketing/${marketingBusinessId}/segments`,
    );

    return segments.data.result as MarketingCampaignSegmentWithFilters[];
  } catch (error) {
    throw new Error('Cannot get segments', { cause: error });
  }
}

export async function createSegment(
  marketingBusinessId: string,
  data: FinalCampaignSegment,
) {
  try {
    const segment = await axios.post(
      `${API_URL}/v1/marketing/${marketingBusinessId}/segments`,
      data,
    );

    return segment.data.result as MarketingCampaignSegmentWithFilters;
  } catch (error) {
    throw new Error('Cannot create segment', { cause: error });
  }
}

export async function invalidateSegments() {
  await client.invalidateQueries({
    queryKey: ['marketing-segments'],
    exact: false,
  });
}

export async function invalidateEmailTemplates() {
  await client.invalidateQueries({
    queryKey: ['marketing-email-templates'],
    exact: false,
  });
}

export async function invalidateTemplates() {
  await client.invalidateQueries({
    queryKey: ['marketing-sms-templates'],
    exact: false,
  });
  await invalidateEmailTemplates();
}

export async function getEmailSenders(marketingBusinessId: string) {
  try {
    const response = await axios.get(
      `${API_URL}/v1/marketing/${marketingBusinessId}/email-senders`,
    );
    return response.data.result as PocketflowsEmailAddress<true>[];
  } catch (error) {
    throw new Error('Cannot get email senders', { cause: error });
  }
}

export async function getBusinessEmailTemplates(marketingBusinessId: string) {
  try {
    const templates = await axios.get(
      `${API_URL}/v1/marketing/${marketingBusinessId}/email-templates`,
    );

    return templates.data.result as {
      projectTemplates: PocketflowsProjectEmailTemplate[];
      templates: MarketingEmailTemplateWithSource[];
    };
  } catch (error) {
    throw new Error('Cannot get business templates', { cause: error });
  }
}

export async function getBusinessSmsTemplates(marketingBusinessId: string) {
  try {
    const templates = await axios.get(
      `${API_URL}/v1/marketing/${marketingBusinessId}/sms-templates`,
    );

    return templates.data.result as {
      templates: MarketingSmsTemplateWithSource[];
    };
  } catch (error) {
    throw new Error('Cannot get business templates', { cause: error });
  }
}

export async function createEmailTemplateAndEmbed(marketingBusinessId: string) {
  try {
    const embed = await axios.post(
      `${API_URL}/v1/marketing/${marketingBusinessId}/email-templates/embeds`,
    );

    return embed.data.result as CreateTemplateAndEmbedResponse;
  } catch (error) {
    throw new Error('Cannot create email template and embed', { cause: error });
  }
}

export async function createSmsTemplateAndEmbed(marketingBusinessId: string) {
  try {
    const embed = await axios.post(
      `${API_URL}/v1/marketing/${marketingBusinessId}/sms-templates/embeds`,
    );

    return embed.data.result as CreateTemplateAndEmbedResponse;
  } catch (error) {
    throw new Error('Cannot create SMS template and embed', { cause: error });
  }
}

export async function createEmailEmbed(
  marketingBusinessId: string,
  emailTemplateId: number,
) {
  try {
    const embed = await axios.post(
      `${API_URL}/v1/marketing/${marketingBusinessId}/email-templates/${emailTemplateId}/embeds`,
    );

    return embed.data.result as CreateTemplateEmbedResponse;
  } catch (error) {
    throw new Error('Cannot create email embed', { cause: error });
  }
}

export async function createSmsEmbed(
  marketingBusinessId: string,
  smsTemplateId: number,
) {
  try {
    const embed = await axios.post(
      `${API_URL}/v1/marketing/${marketingBusinessId}/sms-templates/${smsTemplateId}/embeds`,
    );

    return embed.data.result as CreateTemplateEmbedResponse;
  } catch (error) {
    throw new Error('Cannot create sms embed', { cause: error });
  }
}

export async function deleteEmailTemplate(
  marketingBusinessId: string,
  emailTemplateId: number,
) {
  try {
    await axios.delete(
      `${API_URL}/v1/marketing/${marketingBusinessId}/email-templates/${emailTemplateId}`,
    );

    return null;
  } catch (error) {
    throw new Error('Cannot delete email template', { cause: error });
  }
}

export async function deleteSmsTemplate(
  marketingBusinessId: string,
  smsTemplateId: number,
) {
  try {
    await axios.delete(
      `${API_URL}/v1/marketing/${marketingBusinessId}/sms-templates/${smsTemplateId}`,
    );

    return null;
  } catch (error) {
    throw new Error('Cannot delete sms template', { cause: error });
  }
}

export async function updateEmailTemplate(
  marketingBusinessId: string,
  emailTemplateId: number,
) {
  try {
    const template = await axios.patch(
      `${API_URL}/v1/marketing/${marketingBusinessId}/email-templates/${emailTemplateId}`,
    );

    return template.data.result as MarketingMediumTemplates;
  } catch (error) {
    throw new Error('Cannot update email template', { cause: error });
  }
}

export async function updateSmsTemplate(
  marketingBusinessId: string,
  smsTemplateId: number,
) {
  try {
    const template = await axios.patch(
      `${API_URL}/v1/marketing/${marketingBusinessId}/sms-templates/${smsTemplateId}`,
    );

    return template.data.result as MarketingMediumTemplates;
  } catch (error) {
    throw new Error('Cannot update sms template', { cause: error });
  }
}

export async function copyProjectEmailTemplate(
  marketingBusinessId: string,
  sourceProjectTemplateId: string,
) {
  try {
    const template = await axios.post(
      `${API_URL}/v1/marketing/${marketingBusinessId}/project-email-templates`,
      { sourceTemplateId: sourceProjectTemplateId },
    );

    return template.data.result as CreateTemplateAndEmbedResponse;
  } catch (error) {
    throw new Error('Cannot copy project email template', { cause: error });
  }
}

export async function finalizeCampaign({
  businessId,
  marketingBusinessId,
  campaignId,
}: {
  businessId: string;
  marketingBusinessId: string;
  campaignId: string;
}) {
  try {
    const campaign = await axios.post(
      `${API_URL}/v1/marketing/${marketingBusinessId}/campaign/${campaignId}/finalize`,
      {
        businessId,
      },
    );

    return {
      sourceCampaign: campaign.data.result as PocketflowsCampaign,
    };
  } catch (error) {
    return {
      error: getApiErrorMessage(error, 'Cannot finalize campaign'),
    };
  }
}

export async function getCampaignCost({
  marketingBusinessId,
  marketingCampaignId,
  businessId,
}: {
  marketingBusinessId: string;
  marketingCampaignId: string;
  businessId: string;
}) {
  try {
    const usage = await axios.get(
      `${API_URL}/v1/marketing/${marketingBusinessId}/campaign/${marketingCampaignId}/cost`,
      {
        params: {
          businessId,
        },
      },
    );

    return usage.data.result as CampaignCost;
  } catch (error) {
    throw new Error('Cannot get segment usage', { cause: error });
  }
}

export async function purchaseCredits(payload: PurchaseCreditsPayload) {
  const result = await axios.post(
    `${API_URL}/v1/marketing/${payload.marketingBusinessId}/credits`,
    payload,
  );

  return result.data.result as PaymentCustomerAddOnPayments;
}

export async function getCampaignMetrics(
  marketingBusinessId: string,
  campaignId: string,
) {
  try {
    const result = await axios.get(
      `${API_URL}/v1/marketing/${marketingBusinessId}/campaign/${campaignId}/metrics`,
    );

    return {
      metrics: result.data.result.metrics as CampaignMetrics,
      campaignCost: (result.data.result.campaignCost as number) || 0,
    };
  } catch (error) {
    throw new Error('Cannot get campaign metrics', { cause: error });
  }
}

export async function getEmployeeCredits(marketingBusinessId: string) {
  const result = await axios.get(
    `${API_URL}/v1/marketing/${marketingBusinessId}/credits`,
  );

  return result.data.result as EmployeeCredits;
}

export async function invalidateEmployeeCredits() {
  await client.invalidateQueries({
    queryKey: ['employee-credits'],
    exact: false,
  });
}

export async function updateAdsUrl(
  marketingBusinessId: string,
  adsUrl: string | null,
) {
  const result = await axios.patch(
    `${API_URL}/v1/ads/${marketingBusinessId}/url`,
    { adsUrl },
  );
  return result.data.result as { adsUrl: string | null };
}

export async function invalidateMarketingData() {
  await client.invalidateQueries({
    queryKey: ['marketingData'],
    exact: false,
  });
}
